import type { SnapEvidenceConstraints } from '../types';
import { SnapDenyCodes } from '../types';
import { createSnapError } from '../security/sanitize';

export interface AttachmentPreflightOk {
  ok: true;
  files: File[];
}

export interface AttachmentPreflightFail {
  ok: false;
  error: ReturnType<typeof createSnapError>;
}

function extensionOf(name: string): string {
  const i = name.lastIndexOf('.');
  return i >= 0 ? name.slice(i).toLowerCase() : '';
}

/**
 * Client-side advisory preflight. Server EvidencePipeline remains authoritative.
 */
export function preflightAttachments(
  files: File[] | undefined | null,
  constraints: SnapEvidenceConstraints
): AttachmentPreflightOk | AttachmentPreflightFail {
  const list = files ? [...files] : [];
  const maxCount = constraints.maxAttachmentCount ?? 5;
  if (list.length > maxCount) {
    return {
      ok: false,
      error: createSnapError(
        'attachment_rejected',
        SnapDenyCodes.AttachmentRejected,
        `Too many attachments (max ${maxCount}).`
      )
    };
  }

  const seen = new Set<string>();
  let total = 0;
  const allowedMime = new Set(constraints.allowedMimeTypes.map((m) => m.toLowerCase()));
  const allowedExt = new Set(
    constraints.allowedExtensions.map((e) => e.toLowerCase())
  );

  for (const file of list) {
    const key = `${file.name}|${file.size}|${file.lastModified}`;
    if (seen.has(key)) {
      return {
        ok: false,
        error: createSnapError(
          'attachment_rejected',
          SnapDenyCodes.AttachmentRejected,
          'Duplicate attachment selection is not allowed.'
        )
      };
    }
    seen.add(key);

    if (file.size <= 0) {
      return {
        ok: false,
        error: createSnapError(
          'attachment_rejected',
          SnapDenyCodes.AttachmentRejected,
          'Zero-byte files are not allowed.'
        )
      };
    }
    if (file.size > constraints.maxFileBytes) {
      return {
        ok: false,
        error: createSnapError(
          'attachment_rejected',
          SnapDenyCodes.AttachmentRejected,
          `File '${file.name}' exceeds maximum size.`
        )
      };
    }
    total += file.size;
    if (total > constraints.maxFileBytes) {
      return {
        ok: false,
        error: createSnapError(
          'attachment_rejected',
          SnapDenyCodes.AttachmentRejected,
          'Total attachment size exceeds maximum.'
        )
      };
    }

    const mime = (file.type || '').toLowerCase();
    const ext = extensionOf(file.name);
    if (mime && !allowedMime.has(mime)) {
      return {
        ok: false,
        error: createSnapError(
          'attachment_rejected',
          SnapDenyCodes.AttachmentRejected,
          `MIME type '${mime || 'unknown'}' is not allowed.`
        )
      };
    }
    if (ext && allowedExt.size > 0 && !allowedExt.has(ext)) {
      return {
        ok: false,
        error: createSnapError(
          'attachment_rejected',
          SnapDenyCodes.AttachmentRejected,
          `Extension '${ext}' is not allowed.`
        )
      };
    }
    if (!mime && !ext) {
      return {
        ok: false,
        error: createSnapError(
          'attachment_rejected',
          SnapDenyCodes.AttachmentRejected,
          'Attachment type could not be determined.'
        )
      };
    }
  }

  return { ok: true, files: list };
}
